import { useEffect, useMemo, useState } from 'react'
import Stack from '@mui/joy/Stack'
import Box from '@mui/joy/Box'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Textarea from '@mui/joy/Textarea'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Alert from '@mui/joy/Alert'
import Tooltip from '@mui/joy/Tooltip'
import LinearProgress from '@mui/joy/LinearProgress'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import FolderIcon from '@mui/icons-material/FolderOpen'
import FolderClosedIcon from '@mui/icons-material/Folder'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import {
    FileSearchStoreInfo,
    FileSearchDocInfo,
    FileSearchDocContent,
    FileSearchQueryResult,
    getFileSearchStore,
    listFileSearchDocuments,
    deleteFileSearchDocument,
    searchFileSearch,
    getFileSearchDocumentContent,
} from 'api/oracleApi'

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtBytes(n: number): string {
    if (!Number.isFinite(n) || n <= 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    let v = n
    let i = 0
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024
        i++
    }
    return `${v >= 100 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`
}

function relativeTime(iso?: string): string {
    if (!iso) return '—'
    const diff = Date.now() - new Date(iso).getTime()
    if (!Number.isFinite(diff)) return '—'
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'now'
    if (mins < 60) return `${mins}m`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h`
    const days = Math.floor(hrs / 24)
    if (days < 30) return `${days}d`
    const months = Math.floor(days / 30)
    return `${months}mo`
}

function readMeta(doc: FileSearchDocInfo, key: string): string | undefined {
    const hit = doc.custom_metadata.find((m) => m.key === key)
    if (!hit) return undefined
    if (hit.string_value != null) return hit.string_value
    if (hit.numeric_value != null) return String(hit.numeric_value)
    return undefined
}

function shortName(name: string): string {
    const idx = name.lastIndexOf('/')
    return idx >= 0 ? name.slice(idx + 1) : name
}

// ─── shared primitives ───────────────────────────────────────────────────────

const MONO = 'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace'

function Label({ children, color = 'neutral' }: { children: React.ReactNode; color?: 'neutral' | 'danger' | 'warning' | 'success' }) {
    return (
        <Typography level="body-xs" color={color} fontWeight="md" sx={{ fontFamily: MONO, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10 }}>
            {children}
        </Typography>
    )
}

function HeaderStat({ value, label, color = 'neutral' }: { value: string | number; label: string; color?: 'neutral' | 'success' | 'danger' | 'warning' }) {
    const colorMap = { neutral: 'text.primary', success: 'success.500', danger: 'danger.500', warning: 'warning.500' }
    return (
        <Stack direction="row" gap={0.5} alignItems="baseline" sx={{ px: 1, borderLeft: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontFamily: MONO, fontSize: 13, color: colorMap[color], fontWeight: 600 }}>{value}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 9, color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</Typography>
        </Stack>
    )
}

function StateIcon({ state }: { state: FileSearchDocInfo['state'] }) {
    if (state === 'STATE_ACTIVE') return <CheckCircleIcon sx={{ fontSize: 12, color: 'success.500' }} />
    if (state === 'STATE_PENDING') return <HourglassEmptyIcon sx={{ fontSize: 12, color: 'warning.500' }} />
    if (state === 'STATE_FAILED') return <ErrorIcon sx={{ fontSize: 12, color: 'danger.500' }} />
    return <RadioButtonCheckedIcon sx={{ fontSize: 12, color: 'neutral.400' }} />
}

// ─── document row (within a company folder) ──────────────────────────────────

function DocumentRow({ doc, onDelete }: { doc: FileSearchDocInfo; onDelete: (name: string) => Promise<void> }) {
    const [expanded, setExpanded] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [content, setContent] = useState<FileSearchDocContent | null>(null)
    const [contentLoading, setContentLoading] = useState(false)
    const [contentError, setContentError] = useState('')
    const kind = readMeta(doc, 'doc_kind') ?? '—'
    const factId = readMeta(doc, 'fact_id')
    const category = readMeta(doc, 'category')

    useEffect(() => {
        if (!expanded || content || contentLoading) return
        setContentLoading(true)
        setContentError('')
        getFileSearchDocumentContent(doc.name)
            .then(setContent)
            .catch((err) => setContentError(err instanceof Error ? err.message : String(err)))
            .finally(() => setContentLoading(false))
    }, [expanded, content, contentLoading, doc.name])

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!window.confirm(`Delete ${shortName(doc.name)}?`)) return
        setDeleting(true)
        try {
            await onDelete(doc.name)
        } finally {
            setDeleting(false)
        }
    }

    const kindColor = kind === 'fact' ? 'primary.500' : kind === 'summary' ? 'success.500' : kind === 'source' ? 'warning.500' : 'neutral.500'

    return (
        <Box sx={{ borderBottom: '1px dashed', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
            <Box
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '16px 14px 48px 1fr 60px 36px 22px',
                    alignItems: 'center',
                    gap: 1,
                    pl: 4, // indent under folder
                    pr: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    fontFamily: MONO,
                    fontSize: 11,
                    '&:hover': { bgcolor: 'background.level1' },
                }}
            >
                {expanded ? (
                    <ArrowDropDownIcon sx={{ fontSize: 14, color: 'text.tertiary' }} />
                ) : (
                    <ArrowRightIcon sx={{ fontSize: 14, color: 'text.tertiary' }} />
                )}
                <StateIcon state={doc.state} />
                <Typography sx={{ fontFamily: MONO, fontSize: 9, color: kindColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{kind}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.display_name || shortName(doc.name)}
                    {category && (
                        <Box component="span" sx={{ color: 'text.tertiary', ml: 1, fontSize: 10 }}>
                            · {category}
                        </Box>
                    )}
                </Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary', textAlign: 'right' }}>{fmtBytes(doc.size_bytes)}</Typography>
                <Tooltip title={doc.update_time ?? ''}>
                    <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary', textAlign: 'right' }}>{relativeTime(doc.update_time)}</Typography>
                </Tooltip>
                <IconButton size="sm" variant="plain" color="danger" loading={deleting} onClick={handleDelete} sx={{ '--IconButton-size': '22px' }}>
                    <DeleteIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Box>

            {expanded && (
                <Box sx={{ ml: 4, mr: 1, mb: 1, bgcolor: 'background.level1', borderLeft: '2px solid', borderColor: 'primary.300' }}>
                    <Box sx={{ p: 1.5 }}>
                        <Stack gap={1}>
                            <Stack direction="row" gap={2} flexWrap="wrap">
                                <Stack>
                                    <Label>Resource</Label>
                                    <Typography sx={{ fontFamily: MONO, fontSize: 10, wordBreak: 'break-all' }}>{doc.name}</Typography>
                                </Stack>
                                {factId && (
                                    <Stack>
                                        <Label>fact_id</Label>
                                        <Typography sx={{ fontFamily: MONO, fontSize: 10 }}>{factId}</Typography>
                                    </Stack>
                                )}
                            </Stack>
                            <Stack>
                                <Stack direction="row" gap={1} alignItems="center">
                                    <Label>Content</Label>
                                    {content && (
                                        <Chip
                                            size="sm"
                                            variant="soft"
                                            color={content.source === 'firestore' ? 'success' : 'danger'}
                                            sx={{ fontFamily: MONO, fontSize: 9, height: 16 }}
                                        >
                                            {content.source === 'firestore' ? 'live · firestore' : 'orphaned'}
                                        </Chip>
                                    )}
                                </Stack>
                                {contentLoading && (
                                    <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary', mt: 0.5 }}>$ loading...</Typography>
                                )}
                                {contentError && (
                                    <Alert color="danger" size="sm" sx={{ mt: 0.5 }}>
                                        {contentError}
                                    </Alert>
                                )}
                                {content && (
                                    <Box
                                        sx={{
                                            mt: 0.5,
                                            p: 1.25,
                                            borderRadius: 'sm',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.surface',
                                            fontFamily: MONO,
                                            fontSize: 11,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            maxHeight: 320,
                                            overflow: 'auto',
                                        }}
                                    >
                                        {content.body || '(empty — not in Firestore)'}
                                    </Box>
                                )}
                            </Stack>
                            <Stack>
                                <Label>Custom metadata</Label>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        p: 1,
                                        borderRadius: 'sm',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'background.surface',
                                        fontFamily: MONO,
                                        fontSize: 10,
                                        whiteSpace: 'pre',
                                        overflow: 'auto',
                                    }}
                                >
                                    {doc.custom_metadata.length === 0
                                        ? '(no metadata)'
                                        : doc.custom_metadata
                                              .map((m) => {
                                                  const v =
                                                      m.string_value ??
                                                      (m.numeric_value != null ? String(m.numeric_value) : null) ??
                                                      (m.string_list_value ? m.string_list_value.join(', ') : '')
                                                  return `${m.key.padEnd(14)} = ${v}`
                                              })
                                              .join('\n')}
                                </Box>
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

// ─── company folder ──────────────────────────────────────────────────────────

function CompanyFolder({
    companyId,
    docs,
    onDelete,
    defaultOpen,
}: {
    companyId: string
    docs: FileSearchDocInfo[]
    onDelete: (name: string) => Promise<void>
    defaultOpen: boolean
}) {
    const [open, setOpen] = useState(defaultOpen)
    const totalSize = docs.reduce((s, d) => s + d.size_bytes, 0)
    const factCount = docs.filter((d) => readMeta(d, 'doc_kind') === 'fact').length
    const summaryCount = docs.filter((d) => readMeta(d, 'doc_kind') === 'summary').length
    const sourceCount = docs.filter((d) => readMeta(d, 'doc_kind') === 'source').length

    return (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 'none' } }}>
            <Box
                onClick={() => setOpen((v) => !v)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    cursor: 'pointer',
                    fontFamily: MONO,
                    bgcolor: open ? 'background.level1' : 'transparent',
                    '&:hover': { bgcolor: 'background.level1' },
                }}
            >
                {open ? <ArrowDropDownIcon sx={{ fontSize: 16, color: 'text.tertiary' }} /> : <ArrowRightIcon sx={{ fontSize: 16, color: 'text.tertiary' }} />}
                {open ? <FolderIcon sx={{ fontSize: 14, color: 'warning.500' }} /> : <FolderClosedIcon sx={{ fontSize: 14, color: 'warning.600' }} />}
                <Typography sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: 'text.primary' }}>{companyId}</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary' }}>
                    {docs.length} doc{docs.length !== 1 ? 's' : ''}
                    {summaryCount > 0 && ` · ${summaryCount}s`}
                    {factCount > 0 && ` · ${factCount}f`}
                    {sourceCount > 0 && ` · ${sourceCount}src`}
                    {` · ${fmtBytes(totalSize)}`}
                </Typography>
            </Box>
            {open && (
                <Box sx={{ borderTop: '1px dashed', borderColor: 'divider' }}>
                    {docs.map((d) => (
                        <DocumentRow key={d.name} doc={d} onDelete={onDelete} />
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── main panel ──────────────────────────────────────────────────────────────

export function FileSearchPanel() {
    const [store, setStore] = useState<FileSearchStoreInfo | null>(null)
    const [docs, setDocs] = useState<FileSearchDocInfo[]>([])
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [opResult, setOpResult] = useState('')

    const [kindFilter, setKindFilter] = useState<string>('')
    const [companyFilter, setCompanyFilter] = useState('')

    const [query, setQuery] = useState('')
    const [queryCompany, setQueryCompany] = useState('')
    const [searchRunning, setSearchRunning] = useState(false)
    const [searchResult, setSearchResult] = useState<FileSearchQueryResult | null>(null)

    const fetchAll = async () => {
        setLoading(true)
        setError('')
        try {
            const [s, d] = await Promise.all([getFileSearchStore(), listFileSearchDocuments(200)])
            setStore(s)
            setDocs(d.docs)
            setHasMore(d.has_more)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void fetchAll()
    }, [])

    // Group docs by company_id after applying filters. Sort companies alphabetically;
    // sort docs within each so summary comes first then facts (most useful order).
    const grouped = useMemo(() => {
        const filtered = docs.filter((d) => {
            if (kindFilter) {
                const k = readMeta(d, 'doc_kind') ?? ''
                if (k !== kindFilter) return false
            }
            if (companyFilter.trim()) {
                const c = (readMeta(d, 'company_id') ?? '').toLowerCase()
                if (!c.includes(companyFilter.trim().toLowerCase())) return false
            }
            return true
        })
        const map = new Map<string, FileSearchDocInfo[]>()
        for (const d of filtered) {
            const cid = readMeta(d, 'company_id') ?? '(unattributed)'
            const arr = map.get(cid) ?? []
            arr.push(d)
            map.set(cid, arr)
        }
        const ordered = Array.from(map.entries()).map(([cid, ds]) => {
            ds.sort((a, b) => {
                const ak = readMeta(a, 'doc_kind') ?? 'z'
                const bk = readMeta(b, 'doc_kind') ?? 'z'
                if (ak !== bk) return ak === 'summary' ? -1 : bk === 'summary' ? 1 : ak.localeCompare(bk)
                return (a.display_name ?? a.name).localeCompare(b.display_name ?? b.name)
            })
            return [cid, ds] as const
        })
        ordered.sort(([a], [b]) => a.localeCompare(b))
        return ordered
    }, [docs, kindFilter, companyFilter])

    const handleDeleteDoc = async (name: string) => {
        try {
            await deleteFileSearchDocument(name)
            setDocs((cur) => cur.filter((d) => d.name !== name))
            setOpResult(`deleted ${shortName(name)}`)
            void getFileSearchStore()
                .then(setStore)
                .catch(() => undefined)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        }
    }

    const handleSearch = async () => {
        if (!query.trim()) return
        setSearchRunning(true)
        setSearchResult(null)
        setError('')
        try {
            const r = await searchFileSearch(query.trim(), queryCompany.trim() || undefined)
            setSearchResult(r)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setSearchRunning(false)
        }
    }

    const totalShown = grouped.reduce((s, [, ds]) => s + ds.length, 0)

    return (
        <Stack gap={2}>
            {/* ─── compact terminal header ────────────────────────────────── */}
            <Sheet
                variant="outlined"
                sx={{
                    borderRadius: 'sm',
                    px: 1.5,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontFamily: MONO,
                    bgcolor: 'background.level1',
                }}
            >
                <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'success.500', fontWeight: 600 }}>▮</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'text.primary', fontWeight: 600 }}>file_search</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'text.tertiary' }}>·</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'text.secondary' }}>{store?.display_name ?? '—'}</Typography>
                <Box sx={{ flex: 1 }} />
                <HeaderStat value={store?.active_documents_count ?? '—'} label="active" color="success" />
                <HeaderStat
                    value={store?.pending_documents_count ?? '—'}
                    label="pend"
                    color={store && store.pending_documents_count > 0 ? 'warning' : 'neutral'}
                />
                <HeaderStat
                    value={store?.failed_documents_count ?? '—'}
                    label="fail"
                    color={store && store.failed_documents_count > 0 ? 'danger' : 'neutral'}
                />
                <HeaderStat value={store ? fmtBytes(store.size_bytes) : '—'} label="size" />
                <Tooltip title="Refresh">
                    <IconButton size="sm" variant="plain" color="neutral" onClick={fetchAll} loading={loading} sx={{ '--IconButton-size': '26px' }}>
                        <RefreshIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </Tooltip>
            </Sheet>

            {error && (
                <Alert color="danger" size="sm">
                    {error}
                </Alert>
            )}
            {opResult && (
                <Alert color="success" size="sm">
                    {opResult}
                </Alert>
            )}

            {/* ─── SEARCH PLAYGROUND (top, prominent) ─────────────────────── */}
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 1.5 }}>
                <Stack gap={1}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Label color="success">$ search</Label>
                        <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary' }}>
                            real LLM-grounded query · empty company_id = cross-company
                        </Typography>
                    </Stack>
                    <Textarea
                        minRows={2}
                        placeholder="> which carriers prefer reefer to Dallas?"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={{ fontFamily: MONO, fontSize: 12, '--Textarea-focusedHighlight': 'var(--joy-palette-success-500)' }}
                    />
                    <Stack direction="row" gap={1} alignItems="center">
                        <Input
                            size="sm"
                            placeholder="company_id (optional)"
                            value={queryCompany}
                            onChange={(e) => setQueryCompany(e.target.value)}
                            sx={{ flex: 1, fontFamily: MONO, fontSize: 11 }}
                        />
                        <Button
                            size="sm"
                            color="success"
                            variant="solid"
                            startDecorator={<PlayArrowIcon sx={{ fontSize: 15 }} />}
                            loading={searchRunning}
                            disabled={!query.trim()}
                            onClick={handleSearch}
                            sx={{ fontFamily: MONO, fontSize: 11 }}
                        >
                            run query
                        </Button>
                    </Stack>

                    {searchResult && (
                        <Box sx={{ mt: 1, p: 1.25, borderRadius: 'sm', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}>
                            <Stack gap={1}>
                                <Stack direction="row" gap={1} alignItems="center">
                                    <Label color="success">≫ answer</Label>
                                </Stack>
                                <Typography sx={{ whiteSpace: 'pre-wrap', fontFamily: MONO, fontSize: 12 }}>
                                    {searchResult.answer || '(empty answer)'}
                                </Typography>
                                <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <Label>≫ citations · {searchResult.citations.length}</Label>
                                </Stack>
                                {searchResult.citations.length === 0 ? (
                                    <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary' }}>— none —</Typography>
                                ) : (
                                    <Stack gap={0.75}>
                                        {searchResult.citations.map((c, i) => (
                                            <Box
                                                key={i}
                                                sx={{ p: 0.75, borderRadius: 'sm', border: '1px solid', borderColor: 'divider', bgcolor: 'background.surface' }}
                                            >
                                                <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mb: 0.5 }}>
                                                    <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary' }}>[{i + 1}]</Typography>
                                                    {c.company_id && (
                                                        <Chip size="sm" variant="soft" sx={{ fontFamily: MONO, fontSize: 9, height: 16 }}>
                                                            {c.company_id}
                                                        </Chip>
                                                    )}
                                                    {c.fact_id && (
                                                        <Chip size="sm" variant="soft" color="primary" sx={{ fontFamily: MONO, fontSize: 9, height: 16 }}>
                                                            {c.fact_id}
                                                        </Chip>
                                                    )}
                                                </Stack>
                                                <Typography sx={{ fontFamily: MONO, fontSize: 11, whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </Sheet>

            {/* ─── DOCUMENTS browser with folder grouping ─────────────────── */}
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                <Box
                    sx={{
                        px: 1.5,
                        py: 0.75,
                        bgcolor: 'background.level1',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Label color="success">▾ documents</Label>
                    <Typography sx={{ fontFamily: MONO, fontSize: 10, color: 'text.tertiary' }}>
                        {totalShown}/{docs.length}
                        {hasMore ? ' · more on server' : ''}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Select
                        size="sm"
                        placeholder="kind"
                        value={kindFilter}
                        onChange={(_, v) => setKindFilter(v ?? '')}
                        sx={{ minWidth: 100, fontFamily: MONO, fontSize: 11 }}
                    >
                        <Option value="">all</Option>
                        <Option value="fact">fact</Option>
                        <Option value="summary">summary</Option>
                        <Option value="source">source</Option>
                    </Select>
                    <Input
                        size="sm"
                        placeholder="company_id..."
                        value={companyFilter}
                        onChange={(e) => setCompanyFilter(e.target.value)}
                        sx={{ width: 180, fontFamily: MONO, fontSize: 11 }}
                    />
                </Box>

                {loading && <LinearProgress size="sm" />}

                {docs.length === 0 && !loading ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'text.tertiary' }}>— no documents indexed —</Typography>
                    </Box>
                ) : grouped.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'text.tertiary' }}>— no matches for current filter —</Typography>
                    </Box>
                ) : (
                    <Box>
                        {grouped.map(([cid, ds]) => (
                            <CompanyFolder key={cid} companyId={cid} docs={ds} onDelete={handleDeleteDoc} defaultOpen={grouped.length <= 3} />
                        ))}
                    </Box>
                )}
            </Sheet>
        </Stack>
    )
}
